import { Reorder } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';
import useInfiniteList from '../../../hooks/use-infinite-list';
import useApiRequest from '../../../lib/hooks/useRequest';
import { CourseListItemAdmin } from '../../../lib/interfaces/courses';
import AddIcon from '../../../ui/components/icons/add-icon';
import { Button } from '../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/components/ui/dialog';
import CourseAdminCard, { CourseAdminCardSkeleton } from './components/course-admin-card/course-admin-card';
import CourseFormDialog, { CourseFormSubmitPayload } from './components/course-form-dialog/course-form-dialog';

const PAGE_SIZE = 20;

const LearningSuiteAdminPage = () => {
  const { apiRequest, apiFileRequest } = useApiRequest();

  const buildUrl = useCallback((page: number) => `courses/admin?page=${page}&limit=${PAGE_SIZE}`, []);
  const {
    items: courses,
    setItems: setCourses,
    isLoading,
    isLoadingMore,
    hasMore,
    count,
    sentinelRef,
    reload
  } = useInfiniteList<CourseListItemAdmin>({ buildUrl });

  const [formOpen, setFormOpen] = useState(false);

  const [deleteCandidate, setDeleteCandidate] = useState<CourseListItemAdmin | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreateSubmit = async (payload: CourseFormSubmitPayload) => {
    const createRes = await apiRequest<{ _id: string }>('courses/admin', 'POST', {
      body: payload.data,
      toast: { toastText: 'Kurs wurde erstellt.' }
    });
    const newId = createRes.data?._id;
    if (newId && payload.file) {
      await apiFileRequest(`courses/admin/${newId}/image`, 'POST', payload.file, { formdataName: 'file' });
    }
    await reload();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      await apiRequest(`courses/admin/${deleteCandidate._id}`, 'DELETE', {
        toast: { toastText: 'Kurs wurde gelöscht.' }
      });
      setDeleteCandidate(null);
      await reload();
    } finally {
      setIsDeleting(false);
    }
  };

  // Captured before the list starts shifting, so a drag that ends where it began can be ignored.
  const dragStartIndexRef = useRef<number | null>(null);

  const handleDragStart = (course: CourseListItemAdmin) => {
    dragStartIndexRef.current = courses.findIndex((item) => item._id === course._id);
  };

  /**
   * Loaded courses are always a prefix of the full order — pages arrive from the top, one after the
   * other — so a card's index in this list is its index in the whole set and can be sent as-is.
   */
  const handleDragEnd = async (course: CourseListItemAdmin) => {
    const startIndex = dragStartIndexRef.current;
    dragStartIndexRef.current = null;

    const targetIndex = courses.findIndex((item) => item._id === course._id);
    // A click or a nudge that lands back in place would otherwise rewrite every course's position.
    if (targetIndex === -1 || targetIndex === startIndex) return;

    await apiRequest(`courses/admin/${course._id}/position`, 'PATCH', { body: { targetIndex } });
  };

  return (
    <>
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">Learning Suite Admin</h1>
          <p className="text-[16px] text-[#888]">Kurse verwalten</p>
        </div>
        <Button className="flex gap-3" onClick={() => setFormOpen(true)}>
          <AddIcon />
          <span>Neuer Kurs</span>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <CourseAdminCardSkeleton key={i} />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E4E7EC] p-10 text-center">
          <p className="text-[16px] text-[#12282A] font-bold mb-1">Noch keine Kurse</p>
          <p className="text-[14px] text-[#888]">Lege deinen ersten Kurs an, um loszulegen.</p>
        </div>
      ) : (
        <>
          {/* Reordering the local array is the drag preview; the request follows on drop. */}
          <Reorder.Group axis="y" values={courses} onReorder={setCourses} className="flex flex-col gap-3">
            {courses.map((course) => (
              <CourseAdminCard key={course._id} course={course} onDelete={setDeleteCandidate} sortable onDragStart={handleDragStart} onDragEnd={handleDragEnd} />
            ))}
          </Reorder.Group>

          {/* Sits below the list so it enters the viewport once the user reaches the end. */}
          <div ref={sentinelRef} aria-hidden="true" />

          {isLoadingMore && (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <CourseAdminCardSkeleton key={i} />
              ))}
            </div>
          )}

          {!hasMore && (
            <p className="text-center text-[13px] text-[#888] py-4">
              Alle {count} {count === 1 ? 'Kurs' : 'Kurse'} geladen
            </p>
          )}
        </>
      )}

      <CourseFormDialog isOpen={formOpen} onOpenChange={setFormOpen} onSubmit={handleCreateSubmit} />

      <Dialog open={!!deleteCandidate} onOpenChange={(open) => !open && setDeleteCandidate(null)}>
        <DialogContent className="sm:max-w-md min-w-[440px] bg-white" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[22px] font-bold text-[#12282A]">Kurs wirklich löschen?</DialogTitle>
            <DialogDescription className="text-[14px] text-[#888]">„{deleteCandidate?.title}“ wird unwiderruflich gelöscht.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setDeleteCandidate(null)} disabled={isDeleting}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? 'Löscht…' : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LearningSuiteAdminPage;
